import { httpService } from "../services/HttpService";
import type {
  AuditLogQueryParams,
  AuditLogResponse,
  AuditExportParams,
  ApiResponse,
} from "../types/auditLog.type";

export interface IAuditLogRepository {
  getAuditLogs(params?: AuditLogQueryParams): Promise<AuditLogResponse>;
  exportAuditLogs(params: AuditExportParams): Promise<Blob>;
}

function appendAuditQueryParams(
  queryParams: URLSearchParams,
  params?: AuditLogQueryParams,
): void {
  if (!params) {
    return;
  }

  if (params.page) {
    queryParams.append("page", params.page.toString());
  }
  if (params.limit) {
    queryParams.append("limit", params.limit.toString());
  }
  if (params.actions) {
    queryParams.append("actions", params.actions);
  } else if (params.action) {
    queryParams.append("action", params.action);
  }
  if (params.entityTypes) {
    queryParams.append("entityTypes", params.entityTypes);
  } else if (params.entityType) {
    queryParams.append("entityType", params.entityType);
  }
  if (params.tenantId) {
    queryParams.append("tenantId", params.tenantId);
  }
  if (params.userId) {
    queryParams.append("userId", params.userId);
  }
  if (params.entityId) {
    queryParams.append("entityId", params.entityId);
  }
  if (params.service) {
    queryParams.append("service", params.service);
  }
  if (params.module) {
    queryParams.append("module", params.module);
  }
  if (params.subModule) {
    queryParams.append("subModule", params.subModule);
  }
  if (params.eventCategory) {
    queryParams.append("eventCategory", params.eventCategory);
  }
  if (params.riskTier !== undefined) {
    queryParams.append("riskTier", params.riskTier.toString());
  }
  if (params.studyId) {
    queryParams.append("studyId", params.studyId);
  }
  if (params.siteId) {
    queryParams.append("siteId", params.siteId);
  }
  if (params.subjectId) {
    queryParams.append("subjectId", params.subjectId);
  }
  if (params.correlationId) {
    queryParams.append("correlationId", params.correlationId);
  }
  if (params.sourceType) {
    queryParams.append("sourceType", params.sourceType);
  }
  if (params.status) {
    queryParams.append("status", params.status);
  }
  if (params.performedByRole) {
    queryParams.append("performedByRole", params.performedByRole);
  }
  if (params.part11Relevant !== undefined) {
    queryParams.append("part11Relevant", String(params.part11Relevant));
  }
  if (params.gxpRelevant !== undefined) {
    queryParams.append("gxpRelevant", String(params.gxpRelevant));
  }
  if (params.exportEvent !== undefined) {
    queryParams.append("exportEvent", String(params.exportEvent));
  }
  if (params.integrationEvent !== undefined) {
    queryParams.append("integrationEvent", String(params.integrationEvent));
  }
  if (params.jobEvent !== undefined) {
    queryParams.append("jobEvent", String(params.jobEvent));
  }
  if (params.dateFrom) {
    queryParams.append("dateFrom", params.dateFrom);
  }
  if (params.dateTo) {
    queryParams.append("dateTo", params.dateTo);
  }
  if (params.metadataSearch) {
    queryParams.append("metadataSearch", params.metadataSearch);
  }
}

export class AuditLogRepository implements IAuditLogRepository {
  async getAuditLogs(params?: AuditLogQueryParams): Promise<AuditLogResponse> {
    try {
      const queryParams = new URLSearchParams();
      appendAuditQueryParams(queryParams, params);

      const queryString = queryParams.toString();
      const url = `/audit-logs${queryString ? `?${queryString}` : ""}`;
      const response: ApiResponse<AuditLogResponse> =
        await httpService.get<AuditLogResponse>(url);
      const isSuccess = response.statusCode >= 200 && response.statusCode < 300;
      if (!isSuccess) {
        throw new Error(
          response.error ?? response.message ?? "Failed to fetch audit logs",
        );
      }
      if (!response.data) {
        throw new Error(
          "Request succeeded but no audit log data was returned from the server.",
        );
      }
      return response.data;
    } catch (error: unknown) {
      const err = error as { message?: string; code?: string };
      if (
        err.message?.includes("Network Error") ||
        err.code === "ERR_NETWORK"
      ) {
        return {
          items: [],
          total: 0,
          page: params?.page ?? 1,
          limit: params?.limit ?? 25,
        };
      }
      throw error;
    }
  }

  async exportAuditLogs(params: AuditExportParams): Promise<Blob> {
    const queryParams = new URLSearchParams();
    queryParams.append("format", params.format);
    queryParams.append("exportReason", params.exportReason);

    if (params.columns && params.columns.length > 0) {
      queryParams.append("columns", params.columns.join(","));
    }
    if (params.title) {
      queryParams.append("title", params.title);
    }

    appendAuditQueryParams(queryParams, params);

    const url = `/audit-logs/export?${queryParams.toString()}`;
    return httpService.getBlob(url);
  }
}
